import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import './App.css';
import './styles/common.css';
import SimpleAnimatedBackground from './components/animations/SimpleAnimatedBackground';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import { ErrorBoundary, GlobalLoader, OfflineBanner } from './components/common';
import { setLoadingContext } from './api/axiosInstance';
import { ToastContainer } from 'react-toast'
import { ToastContainer as ToastifyContainer } from 'react-toastify'
import useNotificationSocket from './hooks/useNotificationSocket';
import 'react-toast/src/ToastContainer.css';
import 'react-toast/src/components/Toast/Toast.css';
import 'react-toastify/dist/ReactToastify.css';
import { Suspense } from 'react';
import { ProjectProvider, useProject } from './context/ProjectContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { WorkspaceProvider, useWorkspace } from './context/WorkspaceContext';
import { DialogProvider } from './context/DialogContext';


import OnboardingWizard from './components/wizard/OnboardingWizard';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppRoutes from './Routes';
import './theme/refresh.css';
import './styles/rt2-modal.css';
import {
  fetchLastWorkspaceDashboardTarget,
  isUserAuthenticated,
} from './utils/authRedirectUtils';


export const PrivateRoute = ({ children }) => {
  const isAuthenticated = isUserAuthenticated();
  // Redirect unauthenticated users to /auth/login (not /login)
  return isAuthenticated ? children : <Navigate to="/auth/login" />;
};

export const RedirectIfAuthenticated = ({ children }) => {
  const navigate = useNavigate();
  const { selectWorkspace } = useWorkspace();
  const { selectProject } = useProject();
  const [shouldRender, setShouldRender] = useState(() => !isUserAuthenticated());

  useEffect(() => {
    if (!isUserAuthenticated()) {
      setShouldRender(true);
      return;
    }

    let cancelled = false;

    const redirect = async () => {
      try {
        const { path, lastWorkspace, lastProject } = await fetchLastWorkspaceDashboardTarget();
        if (cancelled) return;

        if (lastWorkspace?.id && lastProject?.project_id) {
          selectWorkspace(lastWorkspace);
          selectProject(String(lastProject.project_id), lastProject.project_name || '');
        }

        navigate(path, { replace: true });
      } catch (error) {
        console.error('Failed to resolve authenticated redirect path:', error);
        if (!cancelled) {
          navigate('/dashboard', { replace: true });
        }
      }
    };

    redirect();

    return () => {
      cancelled = true;
    };
  }, [navigate, selectWorkspace, selectProject]);

  return shouldRender ? children : null;
};

const AppContent = () => {
  const loadingContext = useLoading();
  
  // Initialize notification socket
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // const notificationSocket = useNotificationSocket(user.userId);

  // Initialize axios with loading context
  useEffect(() => {
    setLoadingContext(loadingContext);
  }, [loadingContext]);

  return (
    <>
      {/* <SimpleAnimatedBackground /> */}
      <OfflineBanner />
      <GlobalLoader isVisible={loadingContext.isLoading} />
      <ErrorBoundary>
        <Suspense fallback={<div>Loading...</div>}>
          <AppRoutes />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

// Onboarding Router - delegates to OnboardingWizard
export const OnboardingRouter = () => {
  // Just render the OnboardingWizard which handles all routing logic
  return <OnboardingWizard />;
};

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
      },
    },
  });

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <LoadingProvider>
          <WorkspaceProvider>
            <OnboardingProvider>
              <ProjectProvider>
                <DialogProvider>
                  <AppContent />
                  <ToastContainer
                    position="top-right"
                    delay={3000}
                  />
                  <ToastifyContainer position="top-right" autoClose={3000} newestOnTop closeOnClick />
                </DialogProvider>
              </ProjectProvider>
            </OnboardingProvider>
          </WorkspaceProvider>
        </LoadingProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;